import React from 'react';
import { Select as AntSelect } from 'antd';
import styled from 'styled-components';
import { Colors } from '../../constants';
import { Text } from '../Text';
import { Box } from '../Box';
import { IcTick } from 'basic-ui-lib/icons';

const StyledSelect = styled(AntSelect).attrs({ className: 'custom-select' })`
  height: 48px;

  .ant-select-selector {
    width: 100%;
    padding: 16px;
    border: 1px solid ${Colors.neutral.border[3]}!important;
    border-radius: 4px;
    font-size: 14px;
    transition: all 0.2s ease;
    background: ${Colors.neutral.bg[1]};
    color: ${Colors.neutral.text[6]};

    &:hover {
      border-color: ${Colors.primary[6]}!important;
    }
  }

  &.ant-select-focused .ant-select-selector {
    border-color: ${Colors.primary[6]} !important;
    box-shadow: 0px 0px 0px 3px #3b89901a !important;
  }

  .ant-select-selection-item,
  .ant-select-selection-placeholder {
    font-family: 'Poppins';
    font-weight: 500;
    text-align: left;
    color: ${Colors.neutral.text[5]};
  }

  .ant-select-arrow {
    color: ${Colors.neutral.icon[2]};
  }
`;

interface Option {
  value: string;
  label: string | React.ReactNode;
}

interface SelectProps {
  value?: string;
  options: Option[];
  onChange?: (value: string, option?: Option) => void;
  allowClear?: boolean;
  placeholder?: string;
  style?: React.CSSProperties;
  className?: string;
}

const renderOption = (option: any, currentValue?: string) => {
  return (
    <SelectItem className='option-custom' type='button'>
      {typeof option.label === 'string' ? (
        <Text body_1 regular>
          {option.label}
        </Text>
      ) : (
        option.label
      )}
      <Box display={currentValue === option.value ? 'block' : 'none'}>
        <IcTick fill={Colors.primary[5]} width={16} height={16} />
      </Box>
    </SelectItem>
  );
};

const Select: React.FC<SelectProps> = ({
  value,
  options,
  onChange,
  allowClear = false,
  placeholder = 'Please select',
  style,
  className,
}) => {
  return (
    <StyledSelect
      className={className}
      style={style}
      value={options.find((i) => i.value === value)}
      onChange={(value, option) => onChange?.(value as string, option as Option)}
      allowClear={allowClear}
      placeholder={placeholder}
      optionRender={(option) => renderOption(option, value)}
    >
      {options.map(({ value, label }) => (
        <AntSelect.Option key={value} value={value} className='custom-select-option'>
          {label}
        </AntSelect.Option>
      ))}
    </StyledSelect>
  );
};

export default Select;

const SelectItem = styled.button`
  .ant-select-item:has(&) {
    padding: 8px 0px;

    &[aria-selected='true'] {
      background-color: ${Colors.primary[1]};
      .option-custom {
        .custom-text {
          color: ${Colors.primary[3]};
        }
      }
    }

    .option-custom {
      width: 100%;
      display: flex;
      justify-content: space-between;
      gap: 16px;
      align-items: center;
    }
  }
`;
